import React, { useState } from "react";
import "./../elementPages.less"; // 引入 Less 样式

// 定义类型接口
interface StreamsComponentTableProps {
  source: {
    inputStreams: any[];
    outStreams: any[];
  };
  materials: any[];
}

// 数据模式配置（新增摩尔分率）
const dataModes = [
  { value: "molarFraction", label: "摩尔分率" }, 
  { value: "massFlow", label: "质量流量 （kg/h）" },
  { value: "molarFlow", label: "摩尔流量 （mol/h）" },
  { value: "massConcentration", label: "质量浓度 (mg/m³)" },
];

// 需要筛选的材料名称列表
const FILTERED_MATERIALS = [
  "水", "甲醇", "一氧化碳", "二氧化碳", "甲烷", "氢气", "氮气"
];

const StreamsComponentTable = ({ source, materials }: StreamsComponentTableProps) => {
  // 状态管理
  const [dataMode, setDataMode] = useState("molarFraction");

  // 安全的 toFixed 方法
  const safeToFixed = (value: any, digits: number = 2): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return "0";
    }
    return Number(value).toFixed(digits);
  };

  // 生成表头（筛选指定材料）
  const generateHeaders = () => {
    const headers = ["流股"];
    if (Array.isArray(materials) && materials.length > 0) {
      materials.forEach((material: any) => {
        // if (FILTERED_MATERIALS.includes(material.Name_ZH)) {
          headers.push(material.Molecular_formula || material.Name_ZH);
        // }
      });
    }
    return headers;
  };

  // 生成表格数据
  const generateTableData = (data: any[]) => {
    if (!Array.isArray(data)) return [];

    return data.map((stream: any) => {
      const rowData: any[] = [stream.name || "N/A"];

      if (Array.isArray(materials) && materials.length > 0) {
        materials.forEach((material: any, index: number) => {
          // if (FILTERED_MATERIALS.includes(material.Name_ZH)) {
            let value = 0;
            const massFrac = stream.mass_frac?.[index] || 0;
            const molarFrac = stream.molar_frac?.[index] || 0;
            const fKgPerH = stream.f_kg_per_h || 0;
            const fMolPerH = stream.f_mol_per_h || 0;
            const fNm3PerH = stream.f_nm3_per_h || 1;

            switch (dataMode) {
              case "massFlow":
                // 质量流量：kg/h = 总质量流量 * 质量分数
                value = fKgPerH * massFrac;
                break;
              case "molarFlow":
                // 摩尔流量：mol/h = 总摩尔流量 * 摩尔分数
                value = fMolPerH * molarFrac;
                break;
              case "massConcentration":
                // 质量浓度：mg/m³ = (总质量流量 * 1e6 * 质量分数) / 体积流量
                value = (fKgPerH * 1e6 * massFrac) / fNm3PerH;
                break;
              case "molarFraction":
                // 摩尔分率：直接取数据中的摩尔分数
                value = molarFrac;
                break;
              default:
                value = 0;
            }

            rowData.push(value);
          
        });
      }
      return rowData;
    });
  };

  // 格式化表格数据（处理数字显示）
  const formatTableData = (data: any[]) => {
    return generateTableData(data).map((row: any[]) => {
      return row.map((cell: any, index: number) => {
        // 第一列是流股名称，不需要格式化
        if (index === 0) {
          return cell;
        }
        // 其他列格式化数字
        return safeToFixed(cell);
      });
    });
  };

  const tableHeaders = generateHeaders();

  return (
    <div className="steams-component-table-container">
      {/* 数据模式选择器 */}
      <div className="mode-selector-container">
        <span className="mode-selector-label">显示数据切换：</span>
        <div className="mode-buttons">
          {dataModes.map((mode) => (
            <button
              key={mode.value}
              className={`mode-button ${dataMode === mode.value ? "active" : ""}`}
              onClick={() => setDataMode(mode.value)}
            >
              <span className={`mode-button-text ${dataMode === mode.value ? "active" : ""}`}>
                {mode.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        {/* 输入流股表格 */}
        <h3 className="stream-title">输入流股：</h3>
        <div className="table-wrapper">
          <table className="component-data-table">
            <thead>
              <tr className="table-header">
                {tableHeaders.map((header, index) => (
                  <th key={index} className="table-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formatTableData(source.inputStreams).length > 0 ? (
                formatTableData(source.inputStreams).map((row, rowIndex) => (
                  <tr key={rowIndex} className="table-row">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="table-cell">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan={tableHeaders.length} className="empty-cell">
                    暂无输入流股组分数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 输出流股表格 */}
        <h3 className="stream-title">输出流股：</h3>
        <div className="table-wrapper">
          <table className="component-data-table">
            <thead>
              <tr className="table-header">
                {tableHeaders.map((header, index) => (
                  <th key={index} className="table-header-cell">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formatTableData(source.outStreams).length > 0 ? (
                formatTableData(source.outStreams).map((row, rowIndex) => (
                  <tr key={rowIndex} className="table-row">
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className="table-cell">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan={tableHeaders.length} className="empty-cell">
                    暂无输出流股组分数据
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StreamsComponentTable;